// src/components/CreateSquadModal.jsx
import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import Modal from './Modal';
import notify from '../lib/notify';
import { Button as FcButton, Input as FcInput, Textarea as FcTextarea } from '../ui';

export default function CreateSquadModal({ isOpen, onClose, currentUserId, onSuccess }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [squadCount, setSquadCount] = useState(null);

  // Fetch squad count on mount
  useEffect(() => {
    if (isOpen && currentUserId) {
      fetchSquadCount();
    }
  }, [isOpen, currentUserId]);

  const fetchSquadCount = async () => {
    try {
      const { data, error } = await supabase
        .rpc('get_squad_count', { p_user_id: currentUserId });
      
      if (error) throw error;
      setSquadCount(data || 0);
    } catch (error) {
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      notify('Please enter a squad name', 'error');
      return;
    }

    // Check squad limit
    if (squadCount !== null && squadCount >= 50) {
      notify('You have reached the maximum of 50 squads', 'error');
      return;
    }

    setLoading(true);
    try {
      // Create squad
      const { data: squadData, error: squadError } = await supabase
        .from('squads')
        .insert({
          name: name.trim(),
          description: description.trim() || null,
          created_by: currentUserId
        })
        .select()
        .single();

      if (squadError) {
        // Check for unique constraint violation
        if (squadError.code === '23505') {
          notify('A squad with this name already exists', 'error');
        } else if (squadError.message.includes('Squad limit reached')) {
          notify('You have reached the maximum of 50 squads', 'error');
        } else {
          throw squadError;
        }
        setLoading(false);
        return;
      }

      notify('Squad created successfully!', 'success');
      setName('');
      setDescription('');
      onSuccess();
      onClose();
    } catch (error) {
      notify('Failed to create squad', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title="Create Squad" isOpen={isOpen} onClose={onClose} maxWidth="max-w-lg">
      <div className="p-6">
        <h2 className="text-2xl font-bold text-brand-text mb-4">Create Squad</h2>
        
        {squadCount !== null && (
          <div className="mb-4 p-3 bg-gray-100 rounded-lg">
            <p className="text-sm text-content-primary">
              Your Squads: <span className="font-bold">{squadCount} / 50</span>
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-content-primary mb-2">
              Squad Name *
            </label>
            <FcInput aria-label="Squad name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Sunday League Champions"
              maxLength={50}
              className="w-full border focus:border-transparent"
              required
            />
            <p className="text-xs text-content-secondary mt-1">
              {name.length} / 50 characters
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-content-primary mb-2">
              Description (optional)
            </label>
            <FcTextarea aria-label="What's your squad about?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's your squad about?"
              maxLength={200}
              rows={3}
              className="w-full border focus:border-transparent"
            />
            <p className="text-xs text-content-secondary mt-1">
              {description.length} / 200 characters
            </p>
          </div>

          <div className="bg-surface-canvas border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-ui-green">
              💡 You'll be automatically added as the squad creator and can invite friends after creation.
            </p>
          </div>

          <div className="flex gap-2">
            <FcButton variant="primary" aria-label="Close"
              type="submit"
              disabled={loading || !name.trim()}
              className="flex-1 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Squad'}
            </FcButton>
            <FcButton variant="tertiary"
              type="button"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </FcButton>
          </div>
        </form>
      </div>
    </Modal>
  );
}
